import React from 'react';
import styled from '@emotion/styled';
import type { StayReservation, StaysPolicy } from '../../../interfaces/stays';
import { StatusPill } from '../../status-pill/status-pill';
import { BodyRow, Td } from '../../table/list-table';
import { RowButton, RowDangerButton } from '../dashboard/dashboard-units-components/dashboard-units.styled';
import { RowActions, Sub } from '../dashboard/dashboard-stays-components/dashboard-stays.styled';
import { palette, radius, spacing, typography } from '../../../style-utils/theme';
import { clockTime } from '../../../function-utils/building-time';
import {
  arrivalHelper,
  arrivalText,
  arrivingSoonText,
  checkinPill,
  datesLine,
  firstName,
  guestName,
  isOpenStay,
  partyText,
  sourceLabel,
} from './stays-reservations-model';

/**
 * ONE STAY IN THE HOST'S LIST (ai-plans/stays plan S3). Below md the list
 * table reflows each row to label:value cards (Td data-label).
 *
 * Every action's accessible name carries the guest ("Link for Ana's stay"),
 * so a reader tabbing a list of stays never hears "Link, Link, Link".
 */

const Cell = styled.span`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;
`;

const Name = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;

const Tabular = styled.span`
  font-variant-numeric: tabular-nums;
  overflow-wrap: anywhere;
`;

const PillLine = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.half};
  min-width: 0;
`;

/* The arriving-soon marker: a warning dot beside the pill (decorative; the
   helper line below says it in words). */
const WarnDot = styled.span`
  flex: none;
  width: ${spacing.xs};
  height: ${spacing.xs};
  border-radius: ${radius.circle};
  background: ${palette.warning};
`;

const Helper = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
  overflow-wrap: anywhere;
`;

export interface StayRowProps {
  stay: StayReservation;
  index: number;
  policy?: StaysPolicy | null;
  /** incomplete_alert_hours as a number, or null to show no arriving-soon line. */
  alertHours: number | null;
  /** stays:manage — show the row's actions. */
  canManage: boolean;
  /** "now" for the arriving-soon test (injected in tests). */
  now?: number;
  onLink: (stay: StayReservation) => void;
  onCancel: (stay: StayReservation) => void;
}

export const StayRow: React.FC<StayRowProps> = ({ stay, index, policy, alertHours, canManage, now, onLink, onCancel }) => {
  const first = firstName(stay);
  const pill = checkinPill(stay, policy);
  const incomplete = arrivalHelper(stay);
  const soon = arrivingSoonText(stay, alertHours, now);
  const open = isOpenStay(stay);

  return (
    <BodyRow index={index} data-stay-id={stay.id} data-stay-status={stay.status}>
      <Td data-label="Guest">
        <Cell>
          <Name>{guestName(stay)}</Name>
          <Sub>{[sourceLabel(stay.source), stay.external_uid].filter(Boolean).join(' · ')}</Sub>
        </Cell>
      </Td>
      <Td data-label="Listing">
        <Cell>
          <span>{stay.listing_name}</span>
          <Sub>{stay.unit_label}</Sub>
        </Cell>
      </Td>
      <Td data-label="Dates">
        <Tabular data-check-in={stay.check_in} data-check-out={stay.check_out}>
          {datesLine(stay)}
        </Tabular>
      </Td>
      <Td data-label="Party">{partyText(stay.party_size)}</Td>
      <Td data-label="Arrival">
        <Tabular data-arrival-wall={stay.expected_arrival_time || stay.check_in_time}>{arrivalText(stay)}</Tabular>
        {/* IS7: another account's desk signed this guest in. On the place's clock. */}
        {stay.desk_arrived_at ? (
          <Sub data-testid="stay-desk-arrived" data-at={stay.desk_arrived_at}>
            {`Arrived at the desk ${clockTime(stay.desk_arrived_at, stay.building_timezone)}`}
          </Sub>
        ) : null}
      </Td>
      <Td data-label="Check-in">
        <Cell>
          <PillLine>
            {soon ? <WarnDot aria-hidden="true" data-testid="stay-arriving-soon" /> : null}
            <StatusPill tone={pill.tone} dot data-at={pill.at || undefined}>
              {pill.text}
            </StatusPill>
          </PillLine>
          {incomplete ? <Helper>{incomplete}</Helper> : null}
          {soon ? <Helper>{soon}</Helper> : null}
        </Cell>
      </Td>
      {canManage ? (
        <Td data-label="Actions">
          {open ? (
            <RowActions>
              <RowButton type="button" aria-label={`Link for ${first}'s stay`} onClick={() => onLink(stay)}>
                Link
              </RowButton>
              <RowDangerButton type="button" aria-label={`Cancel stay for ${first}`} onClick={() => onCancel(stay)}>
                Cancel stay
              </RowDangerButton>
            </RowActions>
          ) : (
            '—'
          )}
        </Td>
      ) : null}
    </BodyRow>
  );
};
